import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import { useI18n } from "../lib/i18n";

const COLORS = { exc: "#0d9488", ps: "#d97706", pc: "#e11d48" };

export const PerfectionismResult = ({ scoring, subscales, title }) => {
  const { t } = useI18n();
  if (!scoring) return null;
  return (
    <Card data-testid="perf-result">
      <CardHeader className="pb-2"><CardTitle className="text-base">{title || t("perfScale.yourResult", "Votre résultat")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {["exc", "ps", "pc"].map((k) => {
            const s = scoring.subscales[k];
            return (
              <div key={k} data-testid={`perf-subscale-${k}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{subscales?.[k]?.label || s.label}</span>
                  <span className="text-muted-foreground">{s.mean} / 7</span>
                </div>
                <div className="h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${s.percent}%`, background: COLORS[k] }} /></div>
                {subscales?.[k]?.hint && <p className="text-[11px] text-muted-foreground mt-0.5">{subscales[k].hint}</p>}
              </div>
            );
          })}
        </div>
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
          <p className="text-sm font-semibold" data-testid="perf-profile">{scoring.profile_label}</p>
          <p className="text-sm text-muted-foreground">{scoring.profile_text}</p>
          <p className="text-sm"><span className="font-medium">{t("perfScale.concerns", "Préoccupations")} : {scoring.concerns_level}.</span> <span className="text-muted-foreground">{scoring.concerns_text}</span></p>
        </div>
        <p className="text-[11px] text-muted-foreground">{t("perfScale.disclaimer", "Outil de suivi non normé, inspiré du modèle de l'excellencisme et du perfectionnisme (Gaudreau) et de la FMPS (Frost). Il ne constitue pas un diagnostic ; parlez-en avec votre thérapeute.")}</p>
      </CardContent>
    </Card>
  );
};

export const PerfectionismTrendChart = ({ entries, subscales }) => {
  const { t, locale } = useI18n();
  const data = entries.map((e) => ({
    date: new Date(e.created_at).toLocaleDateString(locale === "en" ? "en-GB" : locale, { day: "numeric", month: "short" }),
    exc: e.scoring.subscales.exc.mean, ps: e.scoring.subscales.ps.mean, pc: e.scoring.subscales.pc.mean,
  }));
  return (
    <Card data-testid="perf-trend-chart">
      <CardHeader className="pb-2"><CardTitle className="text-base">{t("perfScale.trend", "Évolution dans le temps")}</CardTitle></CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis domain={[1, 7]} ticks={[1, 2, 3, 4, 5, 6, 7]} tick={{ fontSize: 11 }} />
              <ReferenceLine y={4.5} stroke="#94a3b8" strokeDasharray="4 4" />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="exc" name={subscales?.exc?.label || "Excellencisme"} stroke={COLORS.exc} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="ps" name={subscales?.ps?.label || "Standards perfectionnistes"} stroke={COLORS.ps} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="pc" name={subscales?.pc?.label || "Préoccupations"} stroke={COLORS.pc} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">{t("perfScale.trendHint", "Objectif : excellencisme stable ou en hausse, standards et préoccupations perfectionnistes en baisse.")}</p>
      </CardContent>
    </Card>
  );
};
